import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import produce from 'immer';
// types
import { ICountDownProps } from './types';
// styles
import styles from './styles.module.scss';
import { toUpper } from 'lodash';
// utils
import { useMemoizedSelector } from '@/hooks/use-memoized-selector';
// models
import { makeSelectEventById } from '@/models/event';

export enum CountdownTimerFormat {
  DAYS_ONLY = 'DAYS_ONLY',
  DAYS_PLUS = 'DAYS_PLUS',
  HOURS_ONLY = 'HOURS_ONLY',
  HOURS_PLUS = 'HOURS_PLUS',
  MINUTES_PLUS = 'MINUTES_PLUS',
  SECONDS_PLUS = 'SECONDS_PLUS',
}
// TODO: implement configurable timer e.g. only hours, minutes seconds

export enum CountdownTimerType {
  CARD = 'CARD',
  NO_CARD = 'NO_CARD',
}

const CountdownTimer = ({
  countdownTil,
  type = CountdownTimerType.CARD,
  templeteType,
  primaryColor,
  currentUtcDateTime = Date.now(),
}: ICountDownProps) => {

  const { eventId } = useParams();
  const event = useMemoizedSelector(makeSelectEventById, eventId);

  const textColor = {
    color: primaryColor,
  };
  const textColorMessage = {
    color: primaryColor,
    fontSize: '28px',
  };

  const msLeft = new Date(countdownTil).getTime() - new Date(currentUtcDateTime).getTime();

  const [intervalId, setIntervalId] = useState();

  const [timerElements, setTimerElements] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    if (!countdownTil) {
      return;
    }

    if (msLeft <= 0) {
      setTimerElements({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
      });

      return;
    }

    const oneDayInMs = 24 * 60 * 60 * 1000;
    const days = Math.floor(msLeft / oneDayInMs);

    const afterDaysMs = msLeft % oneDayInMs;
    const oneHourInMs = 60 * 60 * 1000;
    const hours = Math.floor(afterDaysMs / oneHourInMs);

    const afterHoursMs = afterDaysMs % oneHourInMs;
    const oneMinuteInMs = 60 * 1000;
    const minutes = Math.floor(afterHoursMs / oneMinuteInMs);

    const afterMinutesMs = afterHoursMs % oneMinuteInMs;
    const oneSecondsInMs = 1000;
    const seconds = Math.floor(afterMinutesMs / oneSecondsInMs);

    setTimerElements({
      days,
      hours,
      minutes,
      seconds,
    });
  }, [countdownTil]);

  useEffect(() => {
    const { days, hours, minutes, seconds } = timerElements;
    if (days === 0 && hours === 0 && minutes === 0 && seconds === 0) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
  }, [timerElements]);

  const calculateTimerElements = () => {
    setTimerElements(timerElements =>
      produce(timerElements, draft => {
        const { days, hours, minutes, seconds } = draft;
        if (seconds > 0) {
          draft.seconds = draft.seconds - 1;
          return;
        }

        if (minutes > 0) {
          draft.seconds = 59;
          draft.minutes = minutes - 1;
          return;
        }

        if (hours > 0) {
          draft.seconds = 59;
          draft.minutes = 59;
          draft.hours = hours - 1;
          return;
        }

        if (days > 0) {
          draft.seconds = 59;
          draft.minutes = 59;
          draft.hours = 23;
          draft.days = days - 1;
          return;
        }

        draft.seconds = 0;
        draft.minutes = 0;
        draft.hours = 0;
        draft.days = 0;
      }),
    );
  };

  useEffect(() => {
    if (!countdownTil) {
      return;
    }

    if (msLeft < 0) {
      return;
    }
    const intervalId = setInterval(calculateTimerElements, 1000);
    setIntervalId(intervalId);

    return () => {
      intervalId && clearInterval(intervalId);
    };
  }, [countdownTil]);

  const formatNum = num => {
    if (num < 10) {
      return `0${num}`;
    }

    return `${num}`;
  };

  const now = new Date(currentUtcDateTime);
  const startDateTimeMinus10 = new Date(countdownTil);
  const endDate = new Date(event.endDateTime);
  const endDateTimePlus10 = new Date(event.endDateTime);
  endDateTimePlus10.setMinutes(endDate.getMinutes() + 10);

  if (now > endDateTimePlus10) {
    return (
      <div
        className={
          type === CountdownTimerType.CARD ? styles.timer : styles.timerNoCard
        }
      >
        <div className={styles.timerElement}>
          <div style={textColorMessage} className={styles.number}>
            Event Completed
          </div>
        </div>
      </div>
    );
  }
  
  if (now >= startDateTimeMinus10) {
    return (
      <div
        className={
          type === CountdownTimerType.CARD ? styles.timer : styles.timerNoCard
        }
      >
        <div className={styles.timerElement}>
          <div style={textColorMessage} className={styles.number}>
            The event is now live!
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        type === CountdownTimerType.CARD ? styles.timer : styles.timerNoCard
      }
    >
      {countdownTil &&
        ['days', 'hours', 'minutes', 'seconds'].map(name => {
          const number = timerElements[name];
          return (
            <div key={name} className={styles.timerElement}>
              <div
                className={
                  templeteType === 'bounce'
                    ? styles.bounceNumber
                    : styles.number
                }
                style={textColor}
              >
                {formatNum(number)}
              </div>
              <div
                className={
                  templeteType === 'bounce' ? styles.bounceName : styles.name
                }
                style={textColor}
              >
                {toUpper(name)}
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default React.memo(CountdownTimer);
